import { cn } from '@/lib/tailwind'

interface ImageWithFallbackProps {
  src: string | null | undefined
  alt: string
  aspectRatio?: 'portrait' | 'square'
  fallbackIcon: React.ComponentType<{
    className?: string
    weight?: 'duotone' | 'fill'
  }>
  fallbackColor: string
}

/**
 * An image with an icon fallback.
 *
 * Displays an image at a consistent size, or a styled icon placeholder when
 * there is no image. Nothing about it is specific to media — the name that said
 * so also collided with `capture/media-preview/MediaImage.tsx`.
 */
export function ImageWithFallback({
  src,
  alt,
  aspectRatio = 'portrait',
  fallbackIcon: FallbackIcon,
  fallbackColor,
}: ImageWithFallbackProps) {
  if (!src) {
    return (
      <div
        className={cn(
          'bg-muted/50 flex shrink-0 items-center justify-center rounded-md',
          aspectRatio === 'portrait' ? 'h-24 w-16' : 'h-16 w-16',
        )}
      >
        <FallbackIcon
          className={cn('h-8 w-8', fallbackColor)}
          weight="duotone"
        />
      </div>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className={cn(
        'shrink-0 rounded-md object-cover',
        aspectRatio === 'portrait' ? 'h-24 w-16' : 'h-16 w-16',
      )}
    />
  )
}
